import CartService from "../services/CartService";
import DomUtils from "../utils/dom.utils";
import CheckoutHandler from "./CheckoutHandler";
import DiscountHandler from "./DiscountHandler";
import RemoveGiftsHandler from "./RemoveGifts";

/**
 * Manages the initialization and state observation of the Shopify side cart.
 *
 * Connects and coordinates the logic of discount validation, checkout handling,
 * and promotional gift removal. Observes changes in cart visibility and state
 * to trigger appropriate handlers.
 */
export default class CartHandler {
  constructor() {
    this.domUtils = new DomUtils();
    this.cartService = new CartService();
    this.removeGifts = new RemoveGiftsHandler();
    this.discountHandler = new DiscountHandler(this.domUtils, this.cartService);
    this.checkoutHandler = new CheckoutHandler(this.domUtils, this.cartService);
    this.SELECTORS = {
      cartContainer: "#slidecarthq .slidecarthq",
      emptyClass: "cartEmpty"
    };
  }

  /**
   * Initializes all relevant cart handlers (discount, checkout, gift removal).
   *
   * Skips initialization if the user is already in the checkout flow.
   *
   * @param {HTMLElement} cartContainer - The main DOM element of the side cart.
   * @returns {Promise<void>}
   * @throws {Error} If any of the handlers fail to initialize.
   */
  async initializeCartHandlers(cartContainer) {
    if (location.pathname.startsWith("/checkout")) return;
    try {
      await this.removeGifts.removeGift();
      await this.discountHandler.init(cartContainer);
      await this.checkoutHandler.init(cartContainer);
    } catch (error) {
      await this.removeGifts.removeGift();
      throw new Error(`Error initializing cart handlers: ${error}`);
    }
  }

  /**
   * Observes the cart container for when it becomes visible (adds the "open" class),
   * and removes promotional gift products if needed.
   *
   * @param {HTMLElement} cartContainer - The DOM element of the side cart.
   */
  setupCartOpenObserver(cartContainer) {
    const openObserver = new MutationObserver(async mutations => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class" && cartContainer.classList.contains("open")) {
          try {
            await this.removeGifts.removeGift();
            await this.discountHandler.init(cartContainer);
            await this.checkoutHandler.init(cartContainer);
          } catch (err) {
            await this.removeGifts.removeGift();
            throw new Error(`Error removing gifts on cart open: ${err}`);
          }
        }
      }
    });
    openObserver.observe(cartContainer, {
      attributes: true,
      attributeFilter: ["class"]
    });
  }

  /**
   * Observes changes to the cart container's class list to detect when the cart transitions
   * from empty to non-empty, triggering initialization of cart-related handlers.
   *
   * @param {HTMLElement} cartContainer - The DOM element of the side cart.
   */
  setupCartStateObserver(cartContainer) {
    if (!cartContainer.classList.contains(this.SELECTORS.emptyClass)) {
      this.initializeCartHandlers(cartContainer);
      return;
    }
    const stateObserver = new MutationObserver(async (mutations, obs) => {
      if (!cartContainer.classList.contains(this.SELECTORS.emptyClass)) {
        obs.disconnect();
        this.initializeCartHandlers(cartContainer);
      }
    });
    stateObserver.observe(cartContainer, {
      attributes: true,
      attributeFilter: ["class"]
    });
  }

  /**
   * Entry point for initializing the CartHandler.
   *
   * Waits for the cart container to be available in the DOM, then sets up
   * mutation observers and attempts to remove promotional gifts as a fallback
   * in case earlier logic failed silently.
   *
   * @returns {Promise<void>}
   * @throws {Error} If the cart container is not found or setup fails.
   */
  async initialize() {
    try {
      const cartContainer = (await this.domUtils.waitForElement(this.SELECTORS.cartContainer)) ?? document.querySelector(this.SELECTORS.cartContainer);
      this.setupCartOpenObserver(cartContainer);
      this.setupCartStateObserver(cartContainer);
    } catch (error) {
      /**
       * Add this here in case the gift product is created in the cart before js can validate it,
       * so if this happens there is an internal exception which will not remove the gift.
       */
      await this.removeGifts.removeGift();
      throw new Error(`Error initializing cart optimization: ${error}`);
    }
  }
}